// Force-clear stale service workers and the browser cache storage
// after a fresh deploy. The Emergent prod artefact occasionally
// keeps the previous build's chunk listing alive in a still-
// registered ServiceWorker / CacheStorage entry; visiting a fresh
// ``index.html`` then 404s on chunk filenames the SW believes are
// still valid.
//
// Idempotent: gated on ``localStorage["uavsimnet:clearedFor"]`` so
// each user-agent only runs the clear-and-reload pass ONCE per
// build version. Wrapped in try/catch so it can never break boot.

import { BUILD_VERSION } from "@/buildVersion";

const STORAGE_KEY = "uavsimnet:clearedFor";

export async function forceClearStaleCachesOnce() {
    try {
        if (typeof window === "undefined" || typeof localStorage === "undefined") {
            return false;
        }
        if (localStorage.getItem(STORAGE_KEY) === BUILD_VERSION) {
            return false;
        }

        let didClear = false;

        // 1. Unregister any service workers a previous build left behind.
        if ("serviceWorker" in navigator) {
            try {
                const regs = await navigator.serviceWorker.getRegistrations();
                for (const r of regs) {
                    try {
                        await r.unregister();
                        didClear = true;
                    } catch (_e) {
                        // swallow — defensive
                    }
                }
            } catch (_e) {
                // swallow — defensive
            }
        }

        // 2. Wipe CacheStorage entries (where prior SWs stashed bundles).
        if (typeof caches !== "undefined" && caches.keys) {
            try {
                const keys = await caches.keys();
                for (const k of keys) {
                    try {
                        await caches.delete(k);
                        didClear = true;
                    } catch (_e) {
                        // swallow — defensive
                    }
                }
            } catch (_e) {
                // swallow — defensive
            }
        }

        // 3. Pin the build-version flag so we never re-enter this branch
        //    for this build on this user-agent.
        try {
            localStorage.setItem(STORAGE_KEY, BUILD_VERSION);
        } catch (_e) {
            // swallow — quota or private-mode lockout
        }

        // 4. If we actually unregistered / deleted anything, reload once
        //    so the fresh chunk listing wins the next paint.
        if (didClear) {
            // Defer slightly so the localStorage write commits before
            // the reload obliterates the page.
            setTimeout(() => {
                try { window.location.reload(); } catch (_e) { /* noop */ }
            }, 50);
            return true;
        }
        return false;
    } catch (_outer) {
        // Absolute belt-and-braces — never let this helper kill boot.
        return false;
    }
}
